// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Person Selector Modal for Org Chart
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../../../i18n';
import { fetchPersonas, crearAsignacion } from '../../../shared/services/api';

interface PersonSelectorModalProps {
  puestoId: string;
  puestoTitulo: string;
  onAssigned: () => void;
  onCancel: () => void;
}

export function PersonSelectorModal({
  puestoId,
  puestoTitulo,
  onAssigned,
  onCancel,
}: PersonSelectorModalProps) {
  const { t } = useI18n();
  const [personas, setPersonas] = useState<
    Array<{ id: string; nombre: string; apellidos: string; email: string }>
  >([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPersonas({ pageSize: '200' })
      .then((r) => setPersonas(r.data))
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return personas;
    const q = search.toLowerCase();
    return personas.filter(
      (p) =>
        p.nombre.toLowerCase().includes(q) ||
        p.apellidos.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q),
    );
  }, [personas, search]);

  async function handleSelect(personaId: string) {
    setSaving(true);
    setError('');
    try {
      await crearAsignacion({
        personaId,
        puestoId,
        fte: 1,
        esPrincipal: true,
        fechaInicio: new Date().toISOString().slice(0, 10),
      });
      onAssigned();
    } catch (e: any) {
      setError(e.message ?? t.structure.errorAssignPerson);
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '24px 28px',
          width: 420,
          maxWidth: '90vw',
          maxHeight: '70vh',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <h3
          style={{
            margin: '0 0 4px',
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--color-primario)',
          }}
        >
          {t.structure.assignPersonTitle}
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6B7280' }}>
          {t.structure.puestoLabel}: {puestoTitulo}
        </p>

        {error && (
          <div
            style={{
              background: '#FEF2F2',
              color: '#DC2626',
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t.structure.searchByNameOrEmail}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid #D1D5DB',
            fontSize: 13,
            fontFamily: 'inherit',
            marginBottom: 12,
            boxSizing: 'border-box',
          }}
          autoFocus
        />

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {filtered.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
              {t.structure.noResults}
            </div>
          )}
          {filtered.slice(0, 50).map((p) => (
            <button
              key={p.id}
              onClick={() => handleSelect(p.id)}
              disabled={saving}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                borderRadius: 6,
                transition: 'background 0.1s',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = '#F3F4F6';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 500, color: '#1A1D23' }}>
                {p.nombre} {p.apellidos}
              </span>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>{p.email}</span>
            </button>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid #E2E6ED',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              background: '#fff',
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: 'inherit',
              color: '#5A6171',
            }}
          >
            {t.common.cancelar}
          </button>
        </div>
      </div>
    </div>
  );
}
